import { Box, Card, Flex, Select, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { typography } from 'theme/tokens';

import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { CreateCourtFormAvailabilityInputType, MINUTES_PER_SLOT, SLOTS_PER_HOUR } from '../../../models/court/dto/court-availability-template.constants';
import { SportType } from '../../../models/court/dto/court.dto';
import { WILDCARD_CAPACITY } from '../../../utils/sport-capacity';

const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 0];

// Helper function to format time slot (15-minute slots)
const formatTimeSlot = (slot: number) => {
    const hour = Math.floor(slot / SLOTS_PER_HOUR);
    const minutes = (slot % SLOTS_PER_HOUR) * MINUTES_PER_SLOT;
    return `${hour}:${minutes.toString().padStart(2, '0')}`;
};

type Props = {
    availabilities: CreateCourtFormAvailabilityInputType[];
};

/** What one preview cell holds per matching configuration. */
export type HeatmapConfigEntry = {
    sport: string;
    price?: number;
    currency?: string;
    slotDurationInMs: number;
    capacity?: number;
};

/** Business hours (08:00–20:00), used when the form has no block to measure. */
const DEFAULT_MIN_SLOT = 8 * SLOTS_PER_HOUR;
const DEFAULT_MAX_SLOT = 20 * SLOTS_PER_HOUR;

const configEntryKey = (entry: HeatmapConfigEntry) =>
    `${entry.sport}|${entry.slotDurationInMs}|${entry.capacity ?? ''}|${entry.price ?? ''}|${entry.currency ?? ''}`;

const byPriceThenCapacity = (a: HeatmapConfigEntry, b: HeatmapConfigEntry) =>
    (a.price ?? Number.POSITIVE_INFINITY) - (b.price ?? Number.POSITIVE_INFINITY) ||
    (a.capacity ?? 0) - (b.capacity ?? 0) ||
    a.slotDurationInMs - b.slotDurationInMs ||
    a.sport.localeCompare(b.sport);

/**
 * Add `incoming` to what a slot already holds, cheapest first.
 *
 * Two form blocks legitimately cover the same slot — pricing padel at two
 * capacities is done by adding a second block over the same hours, which is
 * the whole point of multi-capacity pricing. This preview used to `set` the
 * second block's configurations over the first's, so the operator checking
 * their work before saving saw one of the two prices and no sign the other
 * existed. Identical configurations are collapsed; the sort keeps the cell's
 * headline number the cheapest one rather than whichever block was added
 * first.
 */
export function mergeConfigEntries(
    existing: HeatmapConfigEntry[],
    incoming: HeatmapConfigEntry[],
): HeatmapConfigEntry[] {
    const seen = new Set(existing.map(configEntryKey));
    const merged = [...existing];

    incoming.forEach(entry => {
        const key = configEntryKey(entry);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(entry);
    });

    return merged.sort(byPriceThenCapacity);
}

/**
 * The slot columns the grid draws: the opening slot through the LAST BOOKABLE
 * one.
 *
 * `max` is the exclusive end of a block — a court open 08:00–22:00 has `max`
 * at 22:00 and its last bookable slot starts at 21:45 — so the range stops
 * short of it. The grid used to include `max` as a column and paint a price
 * into it, advertising an hour the court is closed.
 */
export function buildAvailabilitySlotRange(
    availabilities: Pick<CreateCourtFormAvailabilityInputType, 'min' | 'max'>[],
): number[] {
    const totalSlotsPerDay = 24 * SLOTS_PER_HOUR;
    let minSlot = totalSlotsPerDay;
    let maxSlot = 0;

    availabilities.forEach(availability => {
        if (availability.min < minSlot) minSlot = availability.min;
        if (availability.max > maxSlot) maxSlot = availability.max;
    });

    // No block, or blocks that cover no slot at all (min >= max): show
    // business hours rather than a grid with no columns.
    if (maxSlot - minSlot <= 0) {
        minSlot = DEFAULT_MIN_SLOT;
        maxSlot = DEFAULT_MAX_SLOT;
    }

    return Array.from({ length: maxSlot - minSlot }, (_, i) => minSlot + i);
}

/** day-of-week -> slot -> every configuration covering that slot. */
export function buildAvailabilityConfigMap(
    availabilities: CreateCourtFormAvailabilityInputType[],
    timeSlots: number[],
): Map<number, Map<number, HeatmapConfigEntry[]>> {
    const map = new Map<number, Map<number, HeatmapConfigEntry[]>>();
    const timeSlotsSet = new Set(timeSlots);

    // Initialize the map with empty values
    DAYS_OF_WEEK.forEach(day => {
        const slotMap = new Map<number, HeatmapConfigEntry[]>();
        timeSlots.forEach(slot => {
            slotMap.set(slot, []);
        });
        map.set(day, slotMap);
    });

    availabilities.forEach(availability => {
        const entries: HeatmapConfigEntry[] = availability.configurations.map(config => ({
            sport: config.sport || 'PADEL',
            price: config.price,
            currency: config.currency,
            slotDurationInMs: config.slotDurationInMs,
            capacity: config.capacity,
        }));

        availability.daysOfWeek.forEach(day => {
            const slotMap = map.get(day);
            if (!slotMap) return;
            // `< max`, not `<= max`: see `buildAvailabilitySlotRange`.
            for (let slot = availability.min; slot < availability.max; slot++) {
                if (!timeSlotsSet.has(slot)) continue;
                slotMap.set(slot, mergeConfigEntries(slotMap.get(slot) ?? [], entries));
            }
        });
    });

    return map;
}

export default function AvailabilitiesHeatmap({ availabilities }: Props) {
    // Get unique sports from all configurations
    const availableSports = useMemo(() => {
        const sportsSet = new Set<SportType>();
        availabilities.forEach(availability => {
            availability.configurations.forEach(config => {
                if (config.sport) {
                    sportsSet.add(config.sport);
                }
            });
        });
        return Array.from(sportsSet);
    }, [availabilities]);

    // Calculate the time slots from availabilities (in 15-minute intervals)
    const TIME_SLOTS = useMemo(() => buildAvailabilitySlotRange(availabilities), [availabilities]);

    // State for selected sport
    const [selectedSport, setSelectedSport] = useState<SportType | 'all'>('all');

    // Create a map of day -> timeSlot -> configurations for quick lookup
    const configMap = useMemo(
        () => buildAvailabilityConfigMap(availabilities, TIME_SLOTS),
        [availabilities, TIME_SLOTS],
    );

    // Helper function to get filtered configurations based on selected sport
    const getFilteredConfigs = (configs: HeatmapConfigEntry[]) => {
        if (selectedSport === 'all') return configs;
        return configs.filter(config => config.sport === selectedSport);
    };

    // Calculate the maximum price for color scaling
    const maxPrice = useMemo(() => {
        let max = 0;
        configMap.forEach(slotMap => {
            slotMap.forEach(configs => {
                const filteredConfigs = getFilteredConfigs(configs);
                filteredConfigs.forEach(config => {
                    if (config.price && config.price > max) max = config.price;
                });
            });
        });
        return max;
    }, [configMap, selectedSport]);

    // Helper function to get color intensity based on price.
    //
    // Shaded by the CHEAPEST matching row, which is the number the cell prints
    // (`filteredConfigs[0]`, sorted by `byPriceThenCapacity`) and the statistic
    // the sibling grid uses (`CourtPricingHeatmap`'s `summarizeCellPricing`).
    // It used to shade by the dearest, so a 2@€30 / 4@€44 slot read "€30+" in
    // the colour of €44 while a uniform €30 slot read "€30" in the colour of
    // €30 — the same printed number, two different shades, on the pre-save
    // preview an operator checks multi-capacity work against.
    const getColorIntensity = (configs: HeatmapConfigEntry[]) => {
        const filteredConfigs = getFilteredConfigs(configs);
        if (maxPrice === 0 || filteredConfigs.length === 0) return 'rgb(241, 241, 241)';
        // Only rows that HAVE a price: a config still being filled in sorts
        // last (its price reads as +Infinity) and the cell prints the cheapest
        // real one, so letting an absent price fall to `0` here would grey out
        // a cell that displays a number.
        const prices = filteredConfigs
            .map(c => c.price)
            .filter((price): price is number => typeof price === 'number' && Number.isFinite(price));
        if (prices.length === 0) return 'rgb(241, 241, 241)';
        const intensity = (Math.min(...prices) / maxPrice) * 0.8; // Max 80% intensity to keep text readable
        return `rgba(66, 153, 225, ${intensity})`; // Chakra blue
    };

    // Helper function to format price
    const formatPrice = (price?: number, currency?: string) => {
        if (price === undefined || price === null || isNaN(price) || !currency) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(price);
    };

    // Helper function to format duration
    const formatDuration = (durationInMs: number) => {
        return durationInMs / 60000;
    };

    // A config with no capacity (or the `-1` sentinel) prices EVERY capacity.
    // Naming it in the tooltip is what tells two same-sport, same-duration
    // blocks apart now that they no longer overwrite each other.
    const formatCapacity = (capacity?: number) => {
        return capacity === undefined || capacity === null || capacity === WILDCARD_CAPACITY
            ? translate('courts.all_capacities')
            : `${capacity} ${translate('game_capacity_options.players')}`;
    };

    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const headerBg = useColorModeValue('white', 'gray.800');
    const cellHeaderBg = useColorModeValue('gray.50', 'gray.700');

    return (
        <Card borderRadius={16} pb={2} alignItems='center' flexDirection='column' w='100%'>
            <Box p={4} w="100%">
                <Flex justify="space-between" align="center" mb={4}>
                    <Text color={textColor} {...typography.pageTitle}>
                        {translate('courts.availabilities')}
                    </Text>
                    <Select
                        value={selectedSport}
                        onChange={(e) => setSelectedSport(e.target.value as SportType | 'all')}
                        width="200px"
                        size="sm"
                    >
                        <option value="all">{translate('courts.all_sports')}</option>
                        {availableSports.map(sport => (
                            <option key={sport} value={sport}>
                                {sport}
                            </option>
                        ))}
                    </Select>
                </Flex>
                <Box
                    maxH="300px"
                    overflowY="auto"
                    overflowX="auto"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    css={{
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#555',
                        },
                    }}
                >
                    <Box minW="fit-content">
                        {/* Header row */}
                        <Box
                            display="flex"
                            position="sticky"
                            top={0}
                            bg={headerBg}
                            zIndex={1}
                            borderBottomWidth="1px"
                            borderColor={borderColor}
                        >
                            {/* Empty corner cell */}
                            <Box
                                minW="40px"
                                h="32px"
                                borderRightWidth="1px"
                                borderColor={borderColor}
                                bg={cellHeaderBg}
                                position="sticky"
                                left={0}
                                zIndex={2}
                            />

                            {/* Time slot headers */}
                            {TIME_SLOTS.map(slot => (
                                <Box
                                    key={slot}
                                    minW="40px"
                                    h="32px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontWeight="bold"
                                    fontSize="xs"
                                    bg={cellHeaderBg}
                                    borderRightWidth="1px"
                                    borderColor={borderColor}
                                >
                                    {formatTimeSlot(slot)}
                                </Box>
                            ))}
                        </Box>

                        {/* Grid rows */}
                        {DAYS_OF_WEEK.map(day => (
                            <Box key={day} display="flex">
                                {/* Day header */}
                                <Box
                                    minW="40px"
                                    h="32px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontWeight="bold"
                                    fontSize="xs"
                                    bg={cellHeaderBg}
                                    borderRightWidth="1px"
                                    borderBottomWidth="1px"
                                    borderColor={borderColor}
                                    position="sticky"
                                    left={0}
                                    zIndex={1}
                                >
                                    {translate(`days.${day}` as any)}
                                </Box>

                                {/* Time slot cells */}
                                {TIME_SLOTS.map(slot => {
                                    const configs = configMap.get(day)?.get(slot) || [];
                                    const filteredConfigs = getFilteredConfigs(configs);
                                    // A slot can hold several prices — that is what pricing
                                    // one sport at two capacities looks like — so the cell
                                    // shows the cheapest and marks that more exist. Without
                                    // the marker the headline number reads as "the" price.
                                    // Counts PRICES, not configurations: a config the
                                    // operator has not priced yet is not a second price,
                                    // and marking it as one would read as "there is a
                                    // higher tariff here".
                                    const hasMultiplePrices =
                                        new Set(
                                            filteredConfigs
                                                .map(config => config.price)
                                                .filter(price => price !== undefined),
                                        ).size > 1;
                                    const tooltipLabel = filteredConfigs.length > 0
                                        ? filteredConfigs.map(config =>
                                            `${config.sport} (${formatCapacity(config.capacity)}): ${formatPrice(config.price, config.currency)} / ${formatDuration(config.slotDurationInMs)} min`
                                        ).join('\n')
                                        : translate('court.no_price');

                                    return (
                                        <Tooltip
                                            key={slot}
                                            label={tooltipLabel}
                                            hasArrow
                                            placement="top"
                                        >
                                            <Box
                                                minW="40px"
                                                h="32px"
                                                bg={getColorIntensity(configs)}
                                                borderRightWidth="1px"
                                                borderBottomWidth="1px"
                                                borderColor={borderColor}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                fontSize="xs"
                                            >
                                                {filteredConfigs.length > 0 && filteredConfigs[0].price !== undefined
                                                    ? `${formatPrice(filteredConfigs[0].price, filteredConfigs[0].currency)}${hasMultiplePrices ? '+' : ''}`
                                                    : '-'
                                                }
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Card>
    );
}